import { formatDays } from "@/lib/format";
import type { LeaveBalance, TierBand } from "./types";

/**
 * A banded balance, said as the ladder rather than as one total.
 *
 * Sick leave is not "90 days": it is 15 days at full pay, then 30 at half,
 * then 45 unpaid, spent in that order. The total is the one number the
 * company asked not to see, because it makes 90 days sound like 90 paid
 * days. So the figure shown is what is left on the rung being spent, and the
 * caption is what comes after it.
 */
export function ladderReading(balance: LeaveBalance): {
  value: string;
  unit: string;
  caption: string;
} | null {
  if (balance.bands.length === 0) return null;
  const entitlement = Number(balance.entitlement_days ?? 0);
  const used = Number(balance.used_days);
  const fixed = balance.bands.filter((band) => band.days !== null);
  const fixedTotal = fixed.reduce((sum, band) => sum + Number(band.days), 0);
  const open = balance.bands.find((band) => band.days === null);
  const openLeft = Math.max(0, entitlement - fixedTotal - Math.max(0, used - fixedTotal));

  const rungs: { label: string; left: number }[] = [
    ...fixed.map((band) => ({ label: band.label.toLowerCase(), left: Number(band.remaining ?? 0) })),
    ...(open ? [{ label: open.label.toLowerCase(), left: openLeft }] : []),
  ];
  const current = rungs.find((rung) => rung.left > 0) ?? rungs[rungs.length - 1]!;
  const after = rungs.slice(rungs.indexOf(current) + 1);

  return {
    value: formatDays(current.left),
    unit: `days at ${current.label}`,
    caption:
      after.length > 0
        ? `then ${after.map((rung) => `${formatDays(rung.left)} ${rung.label}`).join(", then ")}`
        : "the last rung of the ladder",
  };
}

/** The ladder in one line: "15 full pay · 30 half pay · 45 unpaid". */
export function ladderWords(bands: TierBand[], entitlement: number): string {
  const fixed = bands.filter((band) => band.days !== null);
  const fixedTotal = fixed.reduce((sum, band) => sum + Number(band.days), 0);
  const parts = fixed.map((band) => `${formatDays(band.days)} ${band.label.toLowerCase()}`);
  const open = bands.find((band) => band.days === null);
  if (open) parts.push(`${formatDays(Math.max(0, entitlement - fixedTotal))} ${open.label.toLowerCase()}`);
  return parts.join(" · ");
}
